import { useState, useEffect } from "react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import LanguageToggle from "@/components/LanguageToggle";
import SupportWidget from "@/components/SupportWidget";
import TutorialLibrary from "@/components/TutorialLibrary";
import { useLang } from "@/context/LanguageContext";
import LegalFooter from "@/components/LegalFooter";
import { Eye, EyeSlash } from "@phosphor-icons/react";

export default function Login() {
  const { login, user } = useAuth();
  const { tr } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("login"); // login | forgot
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [info, setInfo] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const forgotSubmit = async (e) => {
    e.preventDefault();
    setError(""); setInfo("");
    if (newPassword.length < 8) { setError("La nuova password deve avere almeno 8 caratteri"); return; }
    if (newPassword !== confirmPassword) { setError("Le password non coincidono"); return; }
    setLoading(true);
    try {
      const api = (await import("@/lib/api")).default;
      const r = await api.post("/auth/forgot-password", { email, new_password: newPassword });
      setInfo(r.data?.message || "Password aggiornata. Attendi la riapprovazione di un amministratore.");
      setNewPassword(""); setConfirmPassword("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user) navigate("/", { replace: true }); }, [user, navigate]);

  useEffect(() => {
    try {
      const msg = sessionStorage.getItem("pc_session_msg");
      if (msg) { setInfo(msg); sessionStorage.removeItem("pc_session_msg"); }
    } catch (e) { /* ignore */ }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Signed in");
      navigate("/", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-background">
      <div className="fixed top-4 right-4 z-50"><LanguageToggle /></div>
      {/* Left brand panel */}
      <div
        className="hidden lg:flex lg:w-1/2 relative flex-col justify-between p-12 overflow-hidden"
        style={{ background: "linear-gradient(155deg,#0b1a33 0%,#0e2748 55%,#091528 100%)" }}
      >
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: "repeating-linear-gradient(135deg,hsl(var(--primary)) 0 2px,transparent 2px 24px)" }} />
        <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle,hsl(var(--primary)/0.12),transparent 70%)" }} />
        <div className="relative z-10 font-display font-black uppercase tracking-tight text-2xl text-white">
          Technical<span className="text-primary">Hub</span>
        </div>
        <div className="relative z-10">
          <p className="uppercase text-xs tracking-[0.3em] font-bold text-primary mb-3">
            {tr("Direttore Sportivo, Allenatore, Scouting, Analisi & Area Medica", "Sport Director, Head Coach, Scouting, Analysis & Medical Health")}
          </p>
          <h1 className="font-display font-black uppercase tracking-tighter text-6xl leading-[0.9] text-white">
            Technical
            <br />
            Hub
          </h1>
          <p className="text-white/70 mt-4 max-w-md text-justify whitespace-pre-line">
            {tr(
              "Per il Direttore Sportivo: budget, scadenze contrattuali e composizione liste.\nPer lo Scouting: report, valutazioni e algoritmi che ti aiutano nella ricerca e nelle scelte dei profili adatti al tuo club.\nPer l'Allenatore: analisi partita, piani di allenamento team, individuali e video analisi.\nUn'area Medica e Performance dedicata con anamnesi, storico infortuni, disponibilità dei Players, Wellness & RPE e la Cilindrata di Viscidi per misurare la potenza fisica — all in one.",
              "For the Sport Director: budget, contract deadlines and squad-list composition.\nFor Scouting: reports, ratings and algorithms that support your search and the selection of profiles suited to your club.\nFor the Head Coach: match analysis, team and individual training plans and video analysis.\nA dedicated Medical & Performance area with anamnesis, injury history, player availability, Wellness & RPE and the Cilindrata di Viscidi physical-power index — all in one."
            )}
          </p>
          <p className="text-white/50 mt-4 max-w-md text-sm text-justify">
            {tr(
              "Si integra perfettamente con chi usa piattaforme di scouting e analisi come WyScout, Sics e simili: carichi i tuoi dati nell'hub e noi li rendiamo efficaci.",
              "Integrates seamlessly with users of scouting and analysis platforms like WyScout, Sics and similar: you load your data into the hub and we make it actionable."
            )}
          </p>
        </div>
        <div className="relative z-10 h-1 w-24 bg-primary" />
      </div>

      {/* Right form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 text-center font-display font-black uppercase tracking-tight text-3xl">
            Technical<span className="text-primary">Hub</span>
          </div>
          <p className="uppercase text-xs tracking-[0.2em] font-bold text-muted-foreground mb-2">
            Area riservata
          </p>
          <h2 className="font-display font-black uppercase tracking-tight text-4xl mb-8">
            {mode === "forgot" ? "Reimposta" : "Accedi"}
          </h2>
          {mode === "forgot" ? (
            <form onSubmit={forgotSubmit} className="space-y-5" data-testid="forgot-form">
              <p className="text-sm text-muted-foreground -mt-4">Inserisci la tua email e scegli una nuova password. Il tuo accesso dovrà essere riapprovato da un amministratore prima di poter entrare.</p>
              <div className="space-y-2">
                <Label htmlFor="femail">Email</Label>
                <Input id="femail" type="email" data-testid="forgot-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@club.com" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fnew">Nuova password</Label>
                <Input id="fnew" type="password" data-testid="forgot-newpw-input" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="almeno 8 caratteri" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fconf">Conferma password</Label>
                <Input id="fconf" type="password" data-testid="forgot-confirmpw-input" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" required />
              </div>
              {error && <p className="text-sm text-red-400" data-testid="forgot-error">{error}</p>}
              {info && <p className="text-sm text-emerald-400" data-testid="forgot-info">{info}</p>}
              <Button type="submit" data-testid="forgot-submit-button" disabled={loading} className="w-full font-display font-bold uppercase tracking-wide text-base h-11">
                {loading ? "Invio…" : "Reimposta password"}
              </Button>
              <button type="button" data-testid="back-to-login" onClick={() => { setMode("login"); setError(""); setInfo(""); }} className="text-sm text-primary font-bold hover:underline">← Torna all'accesso</button>
            </form>
          ) : (
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@club.com"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pr-10"
                  required
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? tr("Nascondi password", "Hide password") : tr("Mostra password", "Show password")}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-primary transition-colors"
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            {error && (
              <p className="text-sm text-red-400" data-testid="login-error">
                {error}
              </p>
            )}
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full font-display font-bold uppercase tracking-wide text-base h-11"
            >
              {loading ? tr("Accesso…", "Signing in…") : tr("Accedi", "Sign in")}
            </Button>
            <button type="button" data-testid="go-forgot" onClick={() => { setMode("forgot"); setError(""); setInfo(""); }} className="text-sm text-muted-foreground hover:text-primary hover:underline">{tr("Password dimenticata?", "Forgot password?")}</button>
          </form>
          )}
          {mode !== "forgot" && (
          <p className="text-sm text-muted-foreground mt-6">
            {tr("Nuovo membro dello staff?", "New staff member?")} <Link to="/register" data-testid="go-register" className="text-primary font-bold hover:underline">{tr("Crea un account", "Create an account")}</Link>
          </p>
          )}
          {mode !== "forgot" && (
          <p className="text-sm text-muted-foreground mt-2">
            Vuoi provare Technical Hub per il tuo club? <Link to="/pricing" data-testid="go-pricing" className="text-[#FFD100] font-bold hover:underline">Vedi i piani e prezzi</Link>
          </p>
          )}
          <div className="mt-10 pt-4 border-t border-border/60">
            <div className="flex justify-center gap-4 mb-3">
              <SupportWidget triggerClassName="text-muted-foreground hover:text-primary" />
              <TutorialLibrary triggerClassName="text-muted-foreground hover:text-primary" />
            </div>
            <p data-testid="login-copyright" className="text-[10px] leading-relaxed text-muted-foreground/70 text-center tracking-wide">
              Technical Hub — All rights reserved. Confidential
            </p>
          </div>
          <LegalFooter />
        </div>
      </div>
    </div>
  );
}
